import cx from 'clsx';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import ModalHeader from '@components/modals/ModalHeader/ModalHeader';
import Button from '@components/uikit/Button/Button';
import Checkbox from '@components/uikit/Checkbox/Checkbox';
import Input from '@components/uikit/Input/Input';
import Select from '@components/uikit/Select/Select';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { ITemplate } from '@model/templateItem';

import type { ITemplateCurrenciesList } from '@pages/MultiplicationFactors/model/templateCurrenciesList';
import { useSaveTemplateMutation } from '@pages/MultiplicationFactors/services/useSaveTemplateMutation';
import { useUpdateTemplateMutation } from '@pages/MultiplicationFactors/services/useUpdateTemplateMutation';

import { useModalActions } from '@store/useModalStore';

import {
  COST_SAVING_TEMPLATE_FIELDS,
  type IFormCostSavingTemplate,
  costSavingTemplateDefaultValue,
  schemaCostSavingTemplate,
} from './constants/schemaCostSavingTemplate';
import style from './style.module.scss';

interface IProps {
  currentTemplate?: ITemplate;
  currencies: ITemplateCurrenciesList[];
}

const CostSavingTemplateModal: React.FC<IProps> = ({ currentTemplate, currencies }) => {
  const { t } = useTranslation();

  const { customerId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setModalClose } = useModalActions();

  const {
    control,
    reset,
    handleSubmit,
    setError,
    watch,
    formState: { isDirty, errors },
  } = useForm<IFormCostSavingTemplate>({
    mode: 'onChange',
    resolver: schemaCostSavingTemplate,
    defaultValues: costSavingTemplateDefaultValue(currencies, currentTemplate),
  });

  const { mutate: saveTemplateMutate, isLoading: saveTemplateLoading } = useSaveTemplateMutation();
  const { mutate: updateTemplateMutate, isLoading: updateTemplateLoading } = useUpdateTemplateMutation();

  const [multiplicationFactor] = watch(['multiplication_factor']);

  const closeModal = () => {
    setModalClose();
    reset();
  };

  const submitForm = handleSubmit((data) => {
    currentTemplate ? updateTemplate(data, currentTemplate) : saveTemplate(data);
  });

  const updateTemplate = (formData: IFormCostSavingTemplate, currentTemplate: ITemplate) => {
    const { name, multiplication_factor, recalculate_cost_saving, currency } = formData;
    const template = {
      name,
      multiplication_factor,
      id: currentTemplate.id,
      recalculate_cost_saving: +recalculate_cost_saving,
      currency_code: currency.code,
    };

    updateTemplateMutate(template, {
      onSuccess: () => {
        closeModal();

        showToast({ variant: 'success', message: t('Your changes have been saved.') });
      },
      onError: (error) => {
        if (error.response?.data?.errors?.name) {
          setError('name', { message: error.response.data.errors.name[0] });
        }
      },
    });
  };

  const saveTemplate = (formData: IFormCostSavingTemplate) => {
    const { name, multiplication_factor, currency } = formData;

    const template = {
      name,
      multiplication_factor,
      currency_code: currency.code,
    };

    saveTemplateMutate(template, {
      onSuccess: ({ data }) => {
        const { data: response } = data;

        closeModal();

        showToast({ variant: 'success', message: t('Your changes have been saved.') });

        navigate(`/customer/${customerId}/multiplication-factors/manage-template/${response.model.id}`, {
          state: { created: true, prevPage: pathname },
        });
      },
      onError: (error) => {
        if (error.response?.data?.errors?.name) {
          setError('name', { message: error.response.data.errors.name[0] });
        }
      },
    });
  };

  const isUpdateDisabled = currentTemplate ? updateTemplateLoading || !isDirty : false;

  return (
    <div className={style.costSavingTemplateModal}>
      <ModalHeader onClose={closeModal} />
      <div className={style.modalBody}>
        <div className={style.modalBodyTitle}>
          {currentTemplate ? t('Edit Template') : t(`Adding template`)}
          {currentTemplate && <div>"{currentTemplate?.name || ''}"</div>}
        </div>
        <Input
          name={COST_SAVING_TEMPLATE_FIELDS.NAME}
          control={control}
          label={t('Template Name')}
          required
        />
        <div className={style.multiplicationFactor}>
          <div className={style.multiplicationFactorInputHeader}>
            <label className={style.multiplicationFactorInputLabel}>{t('Multiplication Factor')}</label>
          </div>
          <div
            className={cx(style.multiplicationFactorInputWrapper, {
              [style.multiplicationFactorInputWrapperFilled]: !!multiplicationFactor,
              [style.multiplicationFactorInputWrapperError]: !!errors.multiplication_factor,
            })}
          >
            {!!currencies.length && (
              <div className={style.selectField}>
                <Select
                  name={COST_SAVING_TEMPLATE_FIELDS.CURRENCY}
                  control={control}
                  options={currencies}
                  itemKeys={{ valueFieldKey: 'code', labelFieldKey: 'symbol' }}
                  className={style.multiplicationFactorSelect}
                  type="woHelperText"
                />
              </div>
            )}
            <Input
              control={control}
              name={COST_SAVING_TEMPLATE_FIELDS.MULTIPLICATION_FACTOR}
              required
              inputType="number"
              className={style.multiplicationFactorInput}
              type="woHelperText"
            />
          </div>
          <div className={style.helperText}>{errors.multiplication_factor?.message}</div>
        </div>

        {currentTemplate && (
          <div className={style.recalculation}>
            <Checkbox
              label={t('Recalculate existing action items (open and archive) after changes')}
              name={COST_SAVING_TEMPLATE_FIELDS.RECALCULATE_COST_SAVING}
              control={control}
            />
          </div>
        )}
      </div>
      <div className={style.modalFooter}>
        <Button
          type="primary"
          text={t('Save')}
          disabled={saveTemplateLoading || isUpdateDisabled}
          onClick={submitForm}
        />
        <Button type="secondary" text={t('Cancel')} onClick={closeModal} />
      </div>
    </div>
  );
};

export default CostSavingTemplateModal;
